import { useMutation, useQuery } from '@apollo/client';
import { CloudUpload } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  Input,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { orderBy } from 'lodash';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  initiateFuzzyMatch,
  uploadFuzzyMatchFile,
  generatePresignedUrl,
  uploadFileToS3,
  processUploadedFile,
} from 'src/api/fuzzy-match';
import { useAuthContext } from 'src/auth/hooks';
import Breadcrumbs from 'src/components/custom-breadcrumbs';
import { useSnackbar } from 'src/components/snackbar';
import { TagsAutocomplete } from 'src/components/tags/tags-autocomplete';
import { FUZZY_MATCH_REQUEST_STATUS, FUZZY_MATCH_REQUEST_TYPE } from 'src/constants/fuzzy-match';
import { useOrganizationContext } from 'src/context/organization';
import {
  CREATE_FUZZY_MATCH_REQUEST,
  CREATE_FUZZY_MATCH_TAG_MAPPING,
  SAVE_FUZZY_MATCH_FIELD_MAPPING,
  UPDATE_FUZZY_MATCH_REQUEST,
} from 'src/graphql/fuzzy-match/queries';
import { BULK_CREATE_VOTER_TAGS, GET_ALL_TAGS } from 'src/graphql/voter_tags/queries';
import { useOrganizationVotingAreas } from 'src/hooks/useOrganizationVotingAreas';
import { useRouter } from 'src/routes/hook';
import { OrganizationSelect } from 'src/sections/superadmin/components/organization-select/OrganizationSelect';
import * as XLSX from 'xlsx';

const REQUIRED_FIELDS = [
  { id: 'state_voter_id', label: 'State Voter ID', isPrimary: true },
  { id: 'rnc_reg_id', label: 'RNC Reg ID', isPrimary: true },
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'address', label: 'Address' },
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'zip', label: 'Zip' },
];

export default function TagUploadPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrganizationId } = useOrganizationContext();
  const { user } = useAuthContext();

  const router = useRouter();
  const [createFuzzyMatchRequest] = useMutation(CREATE_FUZZY_MATCH_REQUEST);
  const [saveFuzzyMatchFieldMapping] = useMutation(SAVE_FUZZY_MATCH_FIELD_MAPPING);
  const [createFuzzyMatchTagMapping] = useMutation(CREATE_FUZZY_MATCH_TAG_MAPPING);
  const [updateFuzzyMatchRequest] = useMutation(UPDATE_FUZZY_MATCH_REQUEST);

  const [orgId, setOrgId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagColumns, setTagColumns] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [matchedData, setMatchedData] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);

  // Enhanced tag mapping state
  const [tagMappings, setTagMappings] = useState([]);
  const userRole = user?.users_user_roles[0].user_roles_roles?.code;
  const isSuperadmin = userRole === 'superadmin';

  const organization_id = isSuperadmin ? orgId : selectedOrganizationId;

  const { state } = useOrganizationVotingAreas(organization_id);
  const { data: tagsData, refetch: refetchTags } = useQuery(GET_ALL_TAGS, {
    variables: {
      orgId: organization_id,
    },
    skip: !organization_id,
  });

  const [bulkCreateVoterTags] = useMutation(BULK_CREATE_VOTER_TAGS);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      // Reset all state variables when file is removed
      setSelectedFile(null);
      setExcelData(null);
      setColumnMapping({});
      setAvailableColumns([]);
      setTagColumns([]);
      setTagMappings([]);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      enqueueSnackbar('File size should be less than 50MB', { variant: 'error' });
      return;
    }
    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      enqueueSnackbar('Please upload an Excel file (.xlsx or .xls) or CSV file (.csv)', {
        variant: 'error',
      });
      return;
    }
    setSelectedFile(file);
    processExcelFile(file);
  };

  const processExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        let jsonData;

        if (file.name.endsWith('.csv')) {
          // Process CSV file
          const csvString = new TextDecoder().decode(data);
          const parseCSVRow = (row) => {
            const cells = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"') {
                if (inQuotes && row[i + 1] === '"') {
                  current += '"';
                  i++;
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (char === ',' && !inQuotes) {
                cells.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            cells.push(current.trim());
            return cells;
          };
          const rows = csvString
            .split('\n')
            .filter((row) => row.trim() !== '')
            .map(parseCSVRow);
          jsonData = rows;
        } else {
          // Process Excel file
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        }

        if (jsonData.length < 2) {
          enqueueSnackbar('File must contain at least a header row and one data row', {
            variant: 'error',
          });
          return;
        }

        const headers = jsonData[0];
        setAvailableColumns(headers);
        setExcelData(jsonData.slice(1));

        // Initialize column mapping with empty values
        const initialMapping = {};
        REQUIRED_FIELDS.forEach((field) => {
          initialMapping[field.id] = '';
        });
        setColumnMapping(initialMapping);
        setTagColumns([]);
        setTagMappings([]);
      } catch (error) {
        enqueueSnackbar('Error processing file', { variant: 'error' });
        console.error('Error processing file:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleColumnMappingChange = (fieldId, columnName) => {
    setColumnMapping((prev) => ({
      ...prev,
      [fieldId]: columnName,
    }));
  };

  const handleAddTagColumn = () => {
    setTagColumns((prev) => [
      ...prev,
      { id: `tag_${prev.length + 1}`, label: `Tag ${prev.length + 1}` },
    ]);
  };

  const handleRemoveTagColumn = (tagId) => {
    setTagColumns((prev) => prev.filter((tag) => tag.id !== tagId));
    setColumnMapping((prev) => {
      const newMapping = { ...prev };
      delete newMapping[tagId];
      return newMapping;
    });
    // Also remove tag mappings
    setTagMappings((prev) =>
      prev.filter((mapping) => mapping.column_name !== columnMapping[tagId])
    );
  };

  const handleUpload = async () => {
    // Check which exact ID field is mapped
    const hasStateVoterId = !!columnMapping.state_voter_id;
    const hasRncRegId = !!columnMapping.rnc_reg_id;
    const hasExactId = hasStateVoterId || hasRncRegId;

    // Determine which fields are required based on whether an exact ID is provided
    let requiredFields = REQUIRED_FIELDS;
    if (hasExactId) {
      // Only the mapped exact ID field is required; the other primary field is optional
      requiredFields = REQUIRED_FIELDS.filter((field) =>
        hasStateVoterId ? field.id === 'state_voter_id' : field.id === 'rnc_reg_id'
      );
    } else {
      // If no exact ID, all demographic fields are required
      requiredFields = REQUIRED_FIELDS.filter((field) => !field.isPrimary);
    }

    // Validate that all required fields are mapped
    const unmappedFields = requiredFields.filter((field) => !columnMapping[field.id]);
    if (unmappedFields.length > 0) {
      enqueueSnackbar(
        `Please map all required fields: ${unmappedFields.map((f) => f.label).join(', ')}`,
        { variant: 'error' }
      );
      return;
    }

    if (tagColumns.length === 0) {
      enqueueSnackbar('You must add at least one tag column', { variant: 'error' });
      return;
    }

    // Validate that all tag columns are mapped to tags
    const unmappedTagColumns = tagColumns.filter((tagColumn) => {
      const columnName = columnMapping[tagColumn.id];
      return !tagMappings.some((mapping) => mapping.column_name === columnName);
    });
    if (unmappedTagColumns.length > 0) {
      enqueueSnackbar(
        `Please map all tag columns: ${unmappedTagColumns.map((tc) => tc.label).join(', ')}`,
        { variant: 'error' }
      );
      return;
    }

    let fuzzyMatchRequestId = null;

    try {
      setLoading(true);
      setUploadProgress(0);

      // Step 1: Create a fuzzy match request

      const response = await createFuzzyMatchRequest({
        variables: {
          organization_id: organization_id,
          user_id: user.user_id,
          file_url: selectedFile.name,
          request_type: FUZZY_MATCH_REQUEST_TYPE.TAG_UPLOAD,
          status: FUZZY_MATCH_REQUEST_STATUS.PENDING,
        },
      });

      fuzzyMatchRequestId = response.data.row.returning[0].fuzzy_match_request_id;

      // Step 2: Save the column mappings to the database

      const fieldMapping = Object.entries(columnMapping).map(([key, value]) => ({
        request_id: fuzzyMatchRequestId,
        source_field: key,
        destination_field: value,
        field_type: 'field',
      }));

      await saveFuzzyMatchFieldMapping({
        variables: {
          objects: fieldMapping,
        },
      });

      // Step 3: Create tag mappings
      await createFuzzyMatchTagMapping({
        variables: {
          objects: tagMappings?.map((mapping) => ({
            request_id: fuzzyMatchRequestId,
            column_name: mapping.column_name,
            c_tag_id: mapping.c_tag_id,
            org_tag_id: mapping.org_tag_id,
          })),
        },
      });

      // Step 4: Upload the file to S3
      let fileName = selectedFile.name;
      let uploadResponse;

      // Check file size to determine upload method
      const FILE_SIZE_THRESHOLD = 0; // 0 threshold, force all files to use presigned URL

      if (selectedFile.size > FILE_SIZE_THRESHOLD) {
        // Use presigned URL for large files
        const presignedUrlResponse = await generatePresignedUrl({
          fuzzyMatchRequestId,
          filename: selectedFile.name,
          contentType: selectedFile.type,
        });

        if (presignedUrlResponse.error) {
          throw new Error(presignedUrlResponse.error);
        }

        // Upload directly to S3 using presigned URL
        uploadResponse = await uploadFileToS3(presignedUrlResponse.data.presignedUrl, selectedFile);

        // For presigned URL uploads, the Excel to CSV conversion will happen in the step function
        // So we keep the original filename for now
        fileName = selectedFile.name;
      } else {
        // Use traditional form data upload for smaller files
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('fuzzyMatchRequestId', fuzzyMatchRequestId);

        uploadResponse = await uploadFuzzyMatchFile(formData);
        if (uploadResponse.error) {
          await updateFuzzyMatchRequest({
            variables: {
              request_id: fuzzyMatchRequestId,
              status: FUZZY_MATCH_REQUEST_STATUS.FAILED,
              error_message: String(uploadResponse.error),
            },
          });

          throw new Error(uploadResponse.error);
        }

        if (uploadResponse?.data?.converted && uploadResponse?.data?.uploadedFilename) {
          fileName = uploadResponse?.data?.uploadedFilename;
        }
      }

      // Step 5: Initiate the fuzzy match
      await initiateFuzzyMatch({
        request_id: fuzzyMatchRequestId?.toString(),
        input_csv: fileName,
        output_csv: 'voters_matched.csv',
        staging_csv: 'voters_staging.csv',
        state: state,
      });

      router.push(`/uploads`);
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar('Error uploading file: ' + error.message, { variant: 'error' });
      setProcessingStatus('failed');
      if (fuzzyMatchRequestId) {
        await updateFuzzyMatchRequest({
          variables: {
            request_id: fuzzyMatchRequestId,
            status: FUZZY_MATCH_REQUEST_STATUS.ERROR,
            error_message: String(error.message),
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMatches = async () => {
    if (selectedMatches.length === 0) {
      enqueueSnackbar('Please select at least one match to approve', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);

      // Create tag mapping data for approved matches
      const tagData = [];
      selectedMatches.forEach((matchIndex) => {
        const match = matchedData[matchIndex];
        if (match.voter_id) {
          tagColumns.forEach((tagColumn) => {
            const columnIndex = availableColumns.indexOf(columnMapping[tagColumn.id]);
            const tagValue = match.row[columnIndex];
            if (tagValue) {
              const tagMapping = tagMappings.find(
                (mapping) => mapping.column_name === columnMapping[tagColumn.id]
              );
              if (tagMapping) {
                tagData.push({
                  organization_id: organization_id,
                  voter_id: match.voter_id,
                  c_tag_id: tagMapping.c_tag_id,
                  org_tag_id: tagMapping.org_tag_id,
                });
              }
            }
          });
        }
      });

      // Create tags for approved matches
      await bulkCreateVoterTags({
        variables: {
          data: tagData,
        },
      });

      enqueueSnackbar('Successfully added tags for approved matches', { variant: 'success' });
      setMatchedData(null);
      setSelectedMatches([]);
      setProcessingStatus(null);
    } catch (error) {
      console.error('Error approving matches:', error);
      enqueueSnackbar('Error approving matches: ' + error.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    // Create sample data
    const sampleData = [
      // Headers
      [
        'State Voter ID',
        'RNC Reg ID',
        'First Name',
        'Last Name',
        'Address',
        'City',
        'State',
        'Zip',
        'Tag 1',
        'Tag 2',
        'Tag 3',
      ],
      // Sample rows
      ['123456789', '', 'John', 'Doe', '123 Main St', 'Springfield', 'TX', '12345', 'Yes', '', 'Yes'],
      ['', 'RNC98765432', 'Jane', 'Smith', '456 Oak Ave', 'Riverside', 'CA', '67890', 'Yes', 'Yes', ''],
      ['987654321', '', 'Bob', 'Johnson', '789 Pine Rd', 'Lakeside', 'FL', '54321', '', 'Yes', 'Yes'],
    ];

    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sampleData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sample Data');

    // Generate Excel file and trigger download
    XLSX.writeFile(wb, 'sample_voter_tags.xlsx');
  };

  // Create options for autocomplete
  const createTagOptions = () => {
    const options = [];

    // Add campaign tags
    (tagsData?.ri_apps_prd_c_tags || []).forEach((tag) => {
      options.push({
        label: tag.title,
        value: `c_${tag.c_tag_id}`,
        type: 'c_tag',
        id: tag.c_tag_id,
        title: tag.title,
      });
    });

    // Add organization tags
    (tagsData?.ri_apps_prd_org_tags || []).forEach((tag) => {
      options.push({
        label: tag.title,
        value: `o_${tag.org_tag_id}`,
        type: 'org_tag',
        id: tag.org_tag_id,
        title: tag.title,
      });
    });

    return orderBy(options, 'label');
  };

  const handleTagMappingChange = (tagColumnId, selectedOption) => {
    if (!selectedOption) {
      // Remove mapping if nothing is selected
      setTagMappings((prev) =>
        prev.filter((mapping) => mapping.column_name !== columnMapping[tagColumnId])
      );
      return;
    }

    // Add or update mapping
    setTagMappings((prev) => {
      const existingIndex = prev.findIndex(
        (mapping) => mapping.column_name === columnMapping[tagColumnId]
      );
      const newMapping = {
        column_name: columnMapping[tagColumnId],
        c_tag_id: selectedOption.type === 'c_tag' ? selectedOption.id : null,
        org_tag_id: selectedOption.type === 'org_tag' ? selectedOption.id : null,
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newMapping;
        return updated;
      } else {
        return [...prev, newMapping];
      }
    });
  };

  // Helper function to get the current value for a tag column
  const getTagColumnValue = (tagColumnId) => {
    const columnName = columnMapping[tagColumnId];
    const mapping = tagMappings.find((m) => m.column_name === columnName);

    if (!mapping) return null;

    // Find the tag object from the available options
    const allOptions = createTagOptions();
    return (
      allOptions.find(
        (option) =>
          (mapping.c_tag_id && option.id === mapping.c_tag_id && option.type === 'c_tag') ||
          (mapping.org_tag_id && option.id === mapping.org_tag_id && option.type === 'org_tag')
      ) || null
    );
  };

  // Helper function to generate preview data
  const generatePreviewData = () => {
    if (!excelData || excelData.length === 0) return [];

    const previewRows = [];
    const maxPreviewRows = Math.min(3, excelData.length);

    for (let i = 0; i < maxPreviewRows; i++) {
      const row = excelData[i];
      const previewRow = {};

      // Add mapped field data
      REQUIRED_FIELDS.forEach((field) => {
        const columnIndex = availableColumns.indexOf(columnMapping[field.id]);
        if (columnIndex !== -1 && row[columnIndex] !== undefined) {
          previewRow[field.label] = row[columnIndex];
        } else {
          previewRow[field.label] = '';
        }
      });

      // Add mapped tag data
      tagColumns.forEach((tagColumn) => {
        const columnIndex = availableColumns.indexOf(columnMapping[tagColumn.id]);
        if (columnIndex !== -1 && row[columnIndex] !== undefined) {
          const tagMapping = tagMappings.find(
            (mapping) => mapping.column_name === columnMapping[tagColumn.id]
          );
          if (tagMapping) {
            const tagOption = getTagColumnValue(tagColumn.id);
            previewRow[`${tagColumn.label} (${tagOption?.label || 'Unmapped'})`] = row[columnIndex];
          } else {
            previewRow[`${tagColumn.label} (Unmapped)`] = row[columnIndex];
          }
        } else {
          previewRow[`${tagColumn.label} (Unmapped)`] = '';
        }
      });

      previewRows.push(previewRow);
    }

    return previewRows;
  };

  const previewData = generatePreviewData();

  return (
    <>
      <Container maxWidth="xl">
        <Helmet title="Tag Voters" />
        <Breadcrumbs
          heading="Tag Voters"
          sx={{ mb: { xs: 3, md: 5 } }}
          links={[{ name: 'Uploads', href: '/uploads' }, { name: 'Tag Voters' }]}
        />

        <Stack gap={2}>
          {isSuperadmin && (
            <OrganizationSelect selectedOrganization={orgId} setSelectedOrganization={setOrgId} />
          )}

          {organization_id && (
            <Card sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Upload an Excel file containing voter information and tags
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The Excel file should contain columns for State Voter ID (recommended) or the
                    combination of First Name, Last Name, Address, City, Zip, and State, plus any
                    number of tag columns. After uploading, you'll be able to map the columns to the
                    required fields.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    {!selectedFile ? (
                      <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'fit-content',
                        }}
                      >
                        <CloudUpload
                          sx={{
                            marginRight: 1,
                          }}
                        />
                        Upload File
                        <Input
                          type="file"
                          sx={{
                            clip: 'rect(0 0 0 0)',
                            clipPath: 'inset(50%)',
                            height: 1,
                            overflow: 'hidden',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            whiteSpace: 'nowrap',
                            width: 1,
                          }}
                          onChange={handleFileSelect}
                          value=""
                        />
                      </Button>
                    ) : (
                      <Typography>
                        Attachment:
                        <Chip
                          label={selectedFile.name}
                          size="small"
                          color="info"
                          variant="outlined"
                          onDelete={() => setSelectedFile(null)}
                          sx={{ marginLeft: 1 }}
                        />
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadSample}
                    >
                      Download Sample File
                    </Button>
                  </Stack>
                </Grid>

                {availableColumns.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Map Columns
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {(columnMapping.state_voter_id || columnMapping.rnc_reg_id)
                        ? 'State Voter ID or RNC Reg ID is mapped — just that field is required. Other fields are optional, although we recommend mapping them.'
                        : 'Map the required fields below. If you have the State Voter ID or RNC Reg ID, you can map it to skip other fields.'}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        {REQUIRED_FIELDS.map((field) => {
                          const hasExactIdMapped = columnMapping.state_voter_id || columnMapping.rnc_reg_id;
                          const isRequired = hasExactIdMapped
                            ? (columnMapping.state_voter_id
                                ? field.id === 'state_voter_id'
                                : field.id === 'rnc_reg_id')
                            : !field.isPrimary;
                          const isMapped = !!columnMapping[field.id];

                          return (
                            <Grid item xs={12} sm={6} md={3} key={field.id}>
                              <FormControl fullWidth>
                                <InputLabel>
                                  {field.label}
                                  {isRequired && <span style={{ color: 'red' }}> *</span>}
                                </InputLabel>
                                <Select
                                  value={columnMapping[field.id] || ''}
                                  label={
                                    <>
                                      {field.label}
                                      {isRequired && <span style={{ color: 'red' }}> *</span>}
                                    </>
                                  }
                                  onChange={(e) =>
                                    handleColumnMappingChange(field.id, e.target.value)
                                  }
                                  sx={{
                                    '& .MuiSelect-select': {
                                      color: isRequired && !isMapped ? 'error.main' : 'inherit',
                                    },
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Select a column</em>
                                  </MenuItem>
                                  {availableColumns.map((column) => (
                                    <MenuItem key={column} value={column}>
                                      {column}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          );
                        })}

                        <Grid item xs={12}>
                          <Box sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            Tag Columns
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add columns for each tag you want to map. Each column should contain a
                            single tag value.
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Once you select a column, you can associate it with a tag within the
                            system.
                          </Typography>
                        </Grid>

                        {tagColumns.map((tagColumn) => (
                          <Grid item container spacing={2} xs={12} key={tagColumn.id}>
                            {/* <Stack direction="row" spacing={2} alignItems="flex-start"> */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <InputLabel>{tagColumn.label}</InputLabel>
                                <Select
                                  value={columnMapping[tagColumn.id] || ''}
                                  label={tagColumn.label}
                                  placeholder="Select a tag column"
                                  onChange={(e) =>
                                    handleColumnMappingChange(tagColumn.id, e.target.value)
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select a column</em>
                                  </MenuItem>
                                  {availableColumns.map((column) => (
                                    <MenuItem key={column} value={column}>
                                      {column}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              {/* {columnMapping[tagColumn.id] && ( */}
                              <TagsAutocomplete
                                organizationId={organization_id}
                                label={'Associate Tag'}
                                placeholder="Select or create a tag"
                                value={getTagColumnValue(tagColumn.id)}
                                onChange={(selectedOption) =>
                                  handleTagMappingChange(tagColumn.id, selectedOption)
                                }
                              />
                              {/* )} */}
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveTagColumn(tagColumn.id)}
                                sx={{ mt: 1 }}
                              >
                                Remove
                              </Button>
                            </Grid>
                            {/* </Stack> */}
                          </Grid>
                        ))}

                        <Grid item xs={12}>
                          <Button variant="outlined" onClick={handleAddTagColumn} sx={{ mt: 2 }}>
                            Add Tag Column
                          </Button>
                        </Grid>

                        {tagColumns.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                              Note: Each tag column must be mapped to an existing tag or a new tag
                              must be created before uploading.
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                {/* Preview Section */}
                {previewData.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Data Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Preview of how your data will be mapped. This shows the first{' '}
                      {previewData.length} row(s) of your file.
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {Object.keys(previewData[0] || {}).map((column) => (
                              <TableCell
                                key={column}
                                sx={{ fontWeight: 'bold', backgroundColor: 'background.paper' }}
                              >
                                {column}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.map((row, index) => (
                            <TableRow key={index}>
                              {Object.values(row).map((value, cellIndex) => (
                                <TableCell key={cellIndex}>
                                  {value || <em style={{ color: 'text.secondary' }}>empty</em>}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
                </Grid>
                {availableColumns.length > 0 && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleUpload}
                      disabled={!selectedFile || !excelData || loading}
                    >
                      {loading ? 'Uploading...' : 'Upload Data'}
                    </Button>
                  </Grid>
                )}

                {processingStatus === 'processing' && (
                  <Grid item xs={12}>
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Uploading and processing file... {uploadProgress}%
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {processingStatus === 'completed' && matchedData && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Matched Results
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedMatches(matchedData.map((_, index) => index));
                                  } else {
                                    setSelectedMatches([]);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>First Name</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>Zip</TableCell>
                            <TableCell>Match Confidence</TableCell>
                            <TableCell>Matched Voter ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {matchedData.map((match, index) => (
                            <TableRow key={index}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedMatches.includes(index)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedMatches([...selectedMatches, index]);
                                    } else {
                                      setSelectedMatches(
                                        selectedMatches.filter((i) => i !== index)
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {match.row[availableColumns.indexOf(columnMapping.firstName)]}
                              </TableCell>
                              <TableCell>
                                {match.row[availableColumns.indexOf(columnMapping.lastName)]}
                              </TableCell>
                              <TableCell>
                                {match.row[availableColumns.indexOf(columnMapping.address)]}
                              </TableCell>
                              <TableCell>
                                {match.row[availableColumns.indexOf(columnMapping.city)]}
                              </TableCell>
                              <TableCell>
                                {match.row[availableColumns.indexOf(columnMapping.zip)]}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={match.confidence}
                                  color={
                                    match.confidence === 'exact'
                                      ? 'success'
                                      : match.confidence === 'likely'
                                        ? 'warning'
                                        : 'error'
                                  }
                                />
                              </TableCell>
                              <TableCell>{match.voter_id || 'No match'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleApproveMatches}
                        disabled={selectedMatches.length === 0}
                      >
                        Approve Selected Matches
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}
